import Image from 'next/image';
import { Phone, Mail, MapPin, Facebook } from 'lucide-react';
import Dialog from '@/components/ui/Dialog';
import ShimmerUser from '@/components/ui/ShimmerUser';
import { useMyCv } from '@/features/cv';
import MentorCVProgressBar from './MentorCVProgressBar';
import {
  PRIMARY_BTN,
  PRIMARY_BG,
  TOTAL_CV_FIELDS,
} from '../shared/mentorship.constants';
import type { UserDTO } from '@/shared';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  user: UserDTO | null;
  onLoginRedirect: () => void;
  onEditCv: () => void;
  onSendCv: () => void;
  isSubmitting?: boolean;
};

export default function MentorCVDialog({
  isOpen,
  onClose,
  user,
  onLoginRedirect,
  onEditCv,
  onSendCv,
  isSubmitting = false,
}: Props) {
  const { data: cvDetail } = useMyCv();

  if (!user) {
    return (
      <Dialog
        isOpen={isOpen}
        onClose={onClose}
        title="Бүртгүүлэх"
        maxWidth="lg"
      >
        <div className="flex flex-col gap-4">
          <div className="rounded-lg bg-[#EBF5FB] px-4 py-3 text-sm text-[#0AC1CE]">
            Zangia.mn-д нэвтэрч CV-ээ илгээснээр хөтөлбөрт бүртгүүлнэ.
          </div>
          <ol className="text-sm text-[#414651] space-y-2 list-decimal list-inside">
            <li>Zangia.mn-д нэвтэрч өөрийн CV-ээ илгээнэ.</li>
            <li>Шаардлагатай бол хөтөлбөрийн асуултанд хариулна.</li>
          </ol>
          <div className="flex justify-center">
            <button
              type="button"
              onClick={onLoginRedirect}
              className={PRIMARY_BTN}
              style={{ background: PRIMARY_BG }}
            >
              Zangia.mn-д нэвтрэх
            </button>
          </div>
        </div>
      </Dialog>
    );
  }

  const cvPercent = cvDetail?.cvPercent?.mn ?? 0;
  const filledFields = Math.round((cvPercent / 100) * TOTAL_CV_FIELDS);
  const canSendCv = cvPercent >= 60;

  return (
    <Dialog isOpen={isOpen} onClose={onClose} title="Таны CV" maxWidth="xl">
      <div className="flex flex-col gap-4">
        <div className="border border-gray-200 rounded-xl p-4">
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-full overflow-hidden shrink-0 bg-gray-100">
              <ShimmerUser
                src={user.avatar}
                alt={user.firstname}
                objectID={user.id}
                gender={user.gender ?? 0}
                width={100}
                height={100}
                className="w-full h-full object-cover rounded-full"
              />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-[15px] text-black mb-2">
                {user.lastname} {user.firstname}
              </p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm text-[#535862]">
                {user.phone && (
                  <div className="flex items-center gap-1.5">
                    <Phone size={14} className="shrink-0" />
                    <span className="truncate">{user.phone}</span>
                  </div>
                )}
                {user.facebook && (
                  <div className="flex items-center gap-1.5">
                    <Facebook size={14} className="shrink-0" />
                    <span className="truncate">/{user.facebook}/</span>
                  </div>
                )}
                {user.email && (
                  <div className="flex items-center gap-1.5">
                    <Mail size={14} className="shrink-0" />
                    <span className="truncate">{user.email}</span>
                  </div>
                )}
                {(user.address || user.location_name) && (
                  <div className="flex items-center gap-1.5">
                    <MapPin size={14} className="shrink-0" />
                    <span className="truncate">
                      {user.location_name || user.address}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="border border-gray-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 text-sm font-medium text-black">
              <Image
                src="/icons/icon-document-primary.svg"
                alt=""
                width={16}
                height={16}
              />
              Таны CV явц
            </div>
            <span className="text-sm font-medium text-black">
              {filledFields}{' '}
              <span className="text-gray-400">/ {TOTAL_CV_FIELDS}</span>
            </span>
          </div>
          <MentorCVProgressBar percent={cvPercent} />
          {!canSendCv && (
            <p className="px-3 pb-1 text-xs text-red-500 text-right">
              Таны CV <span className="font-semibold">60%</span> аас дээш байж
              илгээх боломжтой болно
            </p>
          )}
        </div>

        <div className="flex justify-center">
          <button
            type="button"
            onClick={canSendCv ? onSendCv : onEditCv}
            disabled={isSubmitting}
            className={PRIMARY_BTN}
            style={{ background: PRIMARY_BG }}
          >
            {isSubmitting
              ? 'Түр хүлээнэ үү...'
              : canSendCv
                ? 'CV Илгээх'
                : 'CV Засах'}
          </button>
        </div>
      </div>
    </Dialog>
  );
}
